import React, { Fragment, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { useInfiniteQuery, useIsMutating, useMutation, useQuery, useQueryClient } from "react-query"
import { useSelector } from "react-redux"
import { useParams } from "react-router-dom"

import * as Yup from "yup"
import { Card, CardContent, CardHeader, Grid } from "@mui/material"
import { Button, InputText, NotificationUtils, Skeleton, Tab, Tabs, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { FormikProvider, useFormik } from "formik"
import { t } from "i18next"
import { capitalize, isEqual } from "lodash"

import { VersioningDialog } from "../../components/dialogs/VersioningDialog"
import { VersionTag } from "../../components/status/VersionTag"
import { KonanAPI } from "../../services/KonanAPI"
import type { RootState } from "../../store/ReduxStore"
import type { CloneDataFilesRequest, VersionChangeRequest } from "../../types/custom/rules"
import type { ConditionListFile } from "../../types/generated/api/ConditionListFile"
import type { DataFileCloneResponse } from "../../types/generated/api/DataFileCloneResponse"
import type { PaginatedScoreCardVersionsListList } from "../../types/generated/api/PaginatedScoreCardVersionsListList"
import type { ScoreCardCreateUpdate } from "../../types/generated/api/ScoreCardCreateUpdate"
import { ScoreCardCreateUpdateRequest } from "../../types/generated/api/ScoreCardCreateUpdateRequest"
import type { ScoreCardGroupRetrieve } from "../../types/generated/api/ScoreCardGroupRetrieve"
import type { ScoreCardList } from "../../types/generated/api/ScoreCardList"
import type { ScoreCardRetrieve } from "../../types/generated/api/ScoreCardRetrieve"
import type { ScoreCardVersionsList } from "../../types/generated/api/ScoreCardVersionsList"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import { extractScorecardErrorsMessages, getValidMaxMinorVersion } from "../../utils/deploymentDetailsHelpers"
import { extractPageFromBackEndPaginationLink, getEnvVar, isEnvVariableTruthy } from "../../utils/genericHelpers"
import { convertComplexConditionsToNestedForm } from "../../utils/rulesetHelpers"
import { updateComponentsDataFilesIds } from "../ImportFlow/Helpers"
import { COMPONENT_TYPES, type SelectedComponent } from "../Predictions/Interfaces"
import { ComponentTestingDialog } from "../Predictions/components/ComponentTestingDialog"
import { ScorecardLoadingComponent } from "../ScoreCardSets/Scorecardset"
import type { CreateScoreCardRequest, UpdateScorecardRequest } from "../Scorecards/Interfaces"
import { ScorecardMenu } from "../Scorecards/components/Scorecard"
import { ConfigCard } from "./ConfigCard"
import { WeightTable } from "./WeightTable"
import { formatCriteria, isTableFormatFormikValues } from "./helpers"
import type { ScoreTableFormik, ScoreTableProps } from "./interfaces"

import styles from "./ScoreTables.module.scss"

// Yup validation schema
// eslint-disable-next-line react-refresh/only-export-components
export const validationSchema = Yup.object({
  name: Yup.string()
    .min(1)
    .required(t("ScoreTable.validation.nameRequired", { ns: "scorecards" })),
  features: Yup.array()
    .of(Yup.string().required(t("ScoreTable.validation.featureRequired", { ns: "scorecards" })))
    .test({
      test: (arr) => (arr as string[]).length > 0,
    }),
  criteria: Yup.array().of(
    Yup.array().of(
      Yup.object().shape({
        // Only validating the existence of the feature since the rest are being validated in the onSubmit
        feature: Yup.string().required(t("ScoreTable.validation.featureRequired", { ns: "scorecards" })),
      }),
    ),
  ),
})

export function ScoreTable(props: Readonly<ScoreTableProps>): React.ReactElement {
  const {
    variant,
    uuid,
    isAllComponentsLoading,
    scorecard,
    isCreateMode = false,
    handleCancel,
    goToFirstPage,
    disableDuplicateButton,
    duplicateScoreTable,
    isDuplicateMode = false,
    removeComponent,
    setTotalNumOfCurrentProjComponents,
    setCompletedCardsCounter,
    setComponentsErrorMsgs,
    isPermittedToCloneFiles,
    allComponentsDataRef,
    clonedFilesIdsMap,
  } = props

  const { id: projectId } = useParams<{ id: string }>()

  const queryClient = useQueryClient()

  const { t } = useTranslation("scorecards")

  const permissions = useSelector((state: RootState) => state.auth.permissions)
  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canShowDetails = useIsPermitted("Retrieve scorecard", permissions.konan, flattenedKonanPermissions)
  const canRemoveScorecard = useIsPermitted("Delete scorecard", permissions.konan, flattenedKonanPermissions)

  const { ref: lastVersionRef } = useInView({
    onChange: (inView: boolean) => {
      // Fetch next page only when: element is in view AND has more pages AND not currently fetching
      inView && hasNextPage && !isScorecardVersionsListLoading && !isFetchingNextPage && fetchNextPage()
    },
  })

  const RuleTablesEnabledFeatureFlag = isEnvVariableTruthy(String(getEnvVar("RULES_TABLES_ENABLED")))
  const MaxFeaturesFeatureFlag = Number(getEnvVar("RULES_TABLES_MAXIMUM_FEATURES"))

  const [tab, setTab] = useState<"Configuration" | "Weights">(isCreateMode ? "Configuration" : "Weights")

  const [mode, setMode] = useState<"read" | "edit" | "create">(isCreateMode ? "create" : "read")

  const [selectedVersion, setSelectedVersion] = useState<string | undefined>(undefined)
  const [isTestDialogOpen, setIsTestDialogOpen] = useState<boolean>(false)
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState<boolean>(false)

  // Stores the latest minor version for each major version to determine valid restoration points
  // TODO: Move this state into a separate versioning dialog component along with version related logic
  // for better separation of concerns
  const [maxMinorVersionsMap, setMaxMinorVersionsMap] = useState<Record<string, string>>({})

  const lastDuplicateStateRef = useRef<boolean>(false)

  // we need this hook to get the mutation loading state from the react query store
  // because the component keeps unmounting and mounting so until we refactor this part we can not depend on the isLoading
  // property on the mutation because when the component unmount and mounts again we lose the isLoading state
  const isMutating = useIsMutating({
    predicate: (mutation) =>
      mutation.options.mutationKey === "filesClone" + uuid || mutation.options.mutationKey === "componentCreate" + uuid,
  })

  /**
   * Fetches the scorecard data from the server.
   *
   * @returns The scorecard data and loading status.
   */
  const {
    data: scorecardData,
    isLoading: isScorecardDataLoading,
    isRefetching: isScorecardDataRefetching,
  } = useQuery<AxiosResponse<ScoreCardGroupRetrieve>, AxiosError>(
    ["scorecard", scorecard?.uuid],
    async () => {
      if (isDuplicateMode) {
        const data = queryClient.getQueryData<AxiosResponse<ScoreCardGroupRetrieve>>([
          "scorecard",
          projectId,
          scorecard?.uuid,
        ])
        if (data) {
          return data
        }
      }
      return await KonanAPI.fetchScorecard(projectId as string, scorecard?.uuid as string)
    },
    {
      onSuccess: async ({ data }) => {
        const activeVersion = data?.active_version

        if (activeVersion == null || !activeVersion.criteria) return

        const adjustedCriteria = activeVersion?.criteria?.map((criteria) =>
          criteria.map((item) => {
            const { rules } = convertComplexConditionsToNestedForm(item.condition)

            return { parsedCondition: ["", "=", ""], type: "string", ...item, ...rules[0]?.[0] }
          }),
        )

        await formik.setValues({
          name: scorecard?.name ?? data?.name,
          uuid: data?.uuid,
          format: activeVersion?.format?.toLowerCase() ?? "",
          features: activeVersion?.features,
          criteria: adjustedCriteria,
          criteria_files: activeVersion?.criteria_files,
          results: activeVersion?.results || [],
          defaultScore: activeVersion?.default_score,
        })
      },
      // Only enable the query if the scorecard?.uuid is not undefined
      enabled: canShowDetails && !!scorecard?.uuid && variant !== "import",
    },
  )

  /**
   * Fetches the scorecard data from the server.
   *
   * @returns The scorecard data and loading status.
   */
  const {
    isLoading: isScorecardVersionsListLoading,
    isFetchingNextPage,
    data: scorecardVersionsList,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery<AxiosResponse<PaginatedScoreCardVersionsListList>, AxiosError, ScoreCardVersionsList>(
    ["scorecardVersionsList", scorecard?.uuid],
    ({ pageParam = 1 }) =>
      KonanAPI.listScorecardVersions({
        projectId: projectId as string,
        scorecardId: scorecard?.uuid as string,
        page_size: 10,
        page: pageParam,
      }),
    {
      select: ({ pageParams, pages }) => ({
        pageParams: pageParams,
        pages: pages.reduce<ScoreCardVersionsList[]>(
          (accumulator, page) => accumulator.concat(page.data.results as ScoreCardVersionsList[]),
          [],
        ),
      }),
      getNextPageParam: (lastPage) => {
        return lastPage.data?.next ? extractPageFromBackEndPaginationLink(lastPage.data?.next) : false
      },
      enabled: !!projectId && !!scorecard?.uuid && isVersionDialogOpen && variant !== "import",
      refetchOnMount: true,
    },
  )

  // fetch Version
  const { data: scorecardVersionData, isLoading: isScorecardVersionDataLoading } = useQuery<
    AxiosResponse<ScoreCardRetrieve>,
    AxiosError
  >(
    ["selectedScorecardVersion", scorecard?.uuid, selectedVersion],
    () =>
      KonanAPI.retrieveScorecardVersion({
        projectId: projectId as string,
        scorecardId: scorecard?.uuid as string,
        version: selectedVersion as string,
      }),
    {
      onSuccess: async () => {
        if (
          selectedVersion &&
          !maxMinorVersionsMap[selectedVersion?.split(".")?.[0] as string] &&
          scorecardVersionsList?.pages
        ) {
          const result = getValidMaxMinorVersion(scorecardVersionsList?.pages, selectedVersion)
          setMaxMinorVersionsMap((prevResults) => ({
            ...prevResults,
            ...result,
          }))
        }
      },
      enabled: !!projectId && !!scorecard?.uuid && isVersionDialogOpen && !!selectedVersion,
    },
  )

  const createScoreCardMutation = useMutation<
    AxiosResponse,
    AxiosError<CreateScoreCardRequest>,
    CreateScoreCardRequest
  >(KonanAPI.createScorecard, {
    mutationKey: "componentCreate" + uuid,
    onSuccess: async () => {
      await queryClient.invalidateQueries(["scorecards", projectId])
      // closing the scorecard view upon creation
      handleCancel?.()
      NotificationUtils.toast(t("notifications.scorecardCreated"), {
        severity: "success",
      })

      goToFirstPage?.()
    },
    onError: (error: AxiosError<CreateScoreCardRequest>) => {
      if (variant === "import" && uuid) {
        const formattedErrorMessage = capitalize(error?.response?.data?.name?.[0]) || t("scorecardTableCreateError")

        setComponentsErrorMsgs?.((prev) => ({ ...prev, [uuid]: formattedErrorMessage }))
        return
      }

      const errorMessage = extractScorecardErrorsMessages(error?.response?.data, "create")

      NotificationUtils.toast(errorMessage, {
        severity: "error",
      })
    },
  })

  const updateScorecardMutation = useMutation<
    AxiosResponse<ScoreCardCreateUpdate>,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    AxiosError<any>,
    UpdateScorecardRequest
  >(KonanAPI.updateScorecard, {
    onSuccess: async () => {
      await queryClient.invalidateQueries(["scorecards", projectId])
      await queryClient.invalidateQueries(["scorecard", scorecard?.uuid])

      setMode("read")

      NotificationUtils.toast(t("notifications.scorecardUpdated"), {
        severity: "success",
      })
    },
    onError: (response: AxiosError<UpdateScorecardRequest>) => {
      const errorMessage = extractScorecardErrorsMessages(response?.response?.data, "update")

      NotificationUtils.toast(errorMessage, {
        severity: "error",
      })
    },
  })

  const cloneDataFilesMutation = useMutation<AxiosResponse<DataFileCloneResponse[]>, AxiosError, CloneDataFilesRequest>(
    KonanAPI.cloneDataFiles,
    {
      mutationKey: "filesClone" + uuid,
      onSuccess: async ({ data }) => {
        if (!clonedFilesIdsMap) return

        data.forEach((file) => {
          clonedFilesIdsMap[file.source.uuid] = file.uuid
        })

        await formik.setValues((prev) => {
          const updatedComponent = updateComponentsDataFilesIds(prev, clonedFilesIdsMap) as ScoreTableFormik

          return { ...updatedComponent }
        })
      },
    },
  )

  /**
   * Mutation hook for changing the version of a resource.
   *
   * @param {VersionChangeRequest} requestData - The request data for changing the version.
   * @returns {void}
   */
  const versionSwitchMutation = useMutation<AxiosResponse, AxiosError, VersionChangeRequest>(
    KonanAPI.changeResourceVersion,
  )

  const formikInitialValues = (uuid && allComponentsDataRef?.current[uuid]) || {
    uuid: "",
    name: scorecard?.name ?? "",
    format: "table",
    features: ["", ""],
    defaultScore: "0",
    criteria: [
      [
        {
          feature: "",
          condition: "",
          parsedCondition: ["", "=", ""],
          type: "string",
          operator: "equal",
          value: "",
        },
      ],
      [
        {
          feature: "",
          condition: "",
          parsedCondition: ["", "=", ""],
          type: "string",
          operator: "equal",
          value: "",
        },
      ],
    ],
    criteria_files: [] as ConditionListFile[],
    results: [] as string[] | number[],
  }

  const formik = useFormik({
    initialValues: formikInitialValues,
    validationSchema: validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: async (values) => {
      if (!isTableFormatFormikValues(values)) return

      const { name, uuid, criteria, features, results, defaultScore } = values

      // value is split into two parts: before and after the decimal point (if any) to validate the length of each part
      // This is because the backend only accepts 6 digits before the decimal point and 2 digits after
      if (typeof defaultScore === "string") {
        const splitNewValue = defaultScore?.split(".")

        if (splitNewValue[0] && splitNewValue[0].length > 6)
          return NotificationUtils.toast(t("notifications.defaultScoreDigitsBeforeError"), {
            severity: "error",
          })

        if (splitNewValue[1] && splitNewValue[1].length > 2)
          return NotificationUtils.toast(t("notifications.defaultScoreDecimalPlacesError"), {
            severity: "error",
          })
      }

      if (new Set(features).size !== features.length) {
        return NotificationUtils.toast(t("notifications.duplicateFeaturesError"), {
          severity: "error",
        })
      }

      const activeTableVersion = scorecardData?.data?.active_version

      const { adjustedCriteria, adjustedResults } = formatCriteria(criteria, results)

      const shouldSendTableDataWhenUpdating = !(
        isEqual(adjustedCriteria, activeTableVersion?.criteria) &&
        isEqual(features, activeTableVersion?.features) &&
        isEqual(adjustedResults, activeTableVersion?.results)
      )

      if (mode === "create" || variant === "import") {
        await createScoreCardMutation.mutateAsync({
          projectId: projectId as string,
          name: name,
          results: adjustedResults,
          features: features,
          format: ScoreCardCreateUpdateRequest.format.TABLE,
          criteria: adjustedCriteria,
          default_score: defaultScore,
        })

        if (!uuid) return

        removeComponent?.(uuid)
        setCompletedCardsCounter?.((prev) => ++prev)
      } else {
        await updateScorecardMutation.mutateAsync({
          projectId: projectId as string,
          scorecardId: uuid,
          default_score: defaultScore,
          name: name !== scorecardData?.data?.name ? name : undefined,
          ...(shouldSendTableDataWhenUpdating
            ? {
                results: adjustedResults,
                features: features,
                criteria: adjustedCriteria,
              }
            : {}),
        })
      }
    },
  })

  // This Effect to sync the formik changes with the components ref
  // to submit changes with Save all button
  useEffect(() => {
    if (!allComponentsDataRef || !uuid) return

    allComponentsDataRef.current[uuid] = formik.values
  }, [formik.values, uuid, allComponentsDataRef])

  const changeVersion = async (version: string): Promise<void> => {
    try {
      await versionSwitchMutation.mutateAsync({
        projectId: projectId as string,
        resourceId: scorecard?.uuid as string,
        version: version,
        resource: "scorecards",
      })

      NotificationUtils.toast(
        t("notifications.versionRestored", {
          version: version,
        }),
        {
          severity: "success",
        },
      )

      await queryClient.invalidateQueries(["scorecard", scorecard?.uuid])
      await queryClient.invalidateQueries(["scorecardVersionsList", scorecard?.uuid])
    } catch (error) {
      NotificationUtils.toast(
        t("notifications.scorecardsetsRestoreVersionError", {
          version: version,
        }),
        {
          severity: "error",
        },
      )
    }
  }

  const handleCancelEdit = async (): Promise<void> => {
    if (mode === "create") {
      handleCancel?.()

      return
    }

    setMode("read")

    const activeVersion = scorecardData?.data.active_version

    if (!scorecardData || !activeVersion || !activeVersion.criteria) return

    const adjustedCriteria = activeVersion.criteria.map((criteria) =>
      criteria.map((item) => {
        const { rules } = convertComplexConditionsToNestedForm(item.condition)

        return { parsedCondition: ["", "=", ""], type: "string", ...item, ...rules[0]?.[0] }
      }),
    )

    await formik.setValues({
      name: scorecardData?.data.name,
      uuid: scorecardData?.data?.uuid,
      format: activeVersion?.format?.toLowerCase() ?? "",
      features: activeVersion?.features,
      criteria: adjustedCriteria,
      criteria_files: activeVersion?.criteria_files,
      results: activeVersion?.results || [],
      defaultScore: activeVersion?.default_score,
    })
  }

  const handleAddingBucket = async (): Promise<void> => {
    formik.setFieldValue("results", [])

    formik.setFieldValue("criteria", [
      ...formik.values.criteria,
      [
        {
          feature: "",
          condition: "",
          parsedCondition: ["", "=", ""],
          type: "string",
          operator: "equal",
          value: "",
        },
      ],
    ])

    formik.setFieldValue("features", [...formik.values.features, ""])
  }
  // handle clicking the save button for one scoreTable
  const saveImportedScoreTableHandler = async (): Promise<void> => {
    if (!uuid || !clonedFilesIdsMap) return

    setComponentsErrorMsgs?.((prev) => {
      delete prev?.[uuid]
      return { ...prev }
    })

    if (allComponentsDataRef?.current[uuid]?.name.trim() === "") {
      setComponentsErrorMsgs?.((prev) => ({ ...prev, [uuid]: t("notifications.scoreTableNameRquired") }))
      return
    }

    if (formik.values.criteria_files.length > 0) {
      const criteriaFilesIds = new Set<string>()

      const clonedIds = Object.values(clonedFilesIdsMap)

      formik.values.criteria_files.map((file) => {
        if (file.uuid && !clonedIds.includes(file.uuid))
          criteriaFilesIds.add(JSON.stringify({ uuid: file.uuid, version: file.version }))
      })

      if (criteriaFilesIds.size > 0) {
        await cloneDataFilesMutation.mutateAsync({
          project_uuid: projectId as string,
          data_files: Array.from(criteriaFilesIds).map((file) => JSON.parse(file)),
        })
      }
    }

    await formik.submitForm()
  }

  const hasDuplicateFeature = useMemo(() => {
    const filteredFeatures = formik.values.features.filter((feature) => feature !== "").map((feature) => feature.trim())
    const isDuplicate = new Set(filteredFeatures).size !== filteredFeatures.length

    // Only show notification if:
    // 1. There is a duplicate now
    // 2. There wasn't a duplicate before (transition from no duplicates to duplicates)
    if (isDuplicate && !lastDuplicateStateRef.current) {
      lastDuplicateStateRef.current = true
      NotificationUtils.toast(t("notifications.duplicateFeaturesError"), {
        severity: "error",
      })
    } else if (!isDuplicate && lastDuplicateStateRef.current) {
      // Reset when duplicates are resolved
      lastDuplicateStateRef.current = false
    }

    return isDuplicate
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.features])

  // TODO:: refactor later
  // children component for the versioning dialog for scorecard
  const ScoreTableVersioningContainer = (): React.ReactElement => {
    const [dialogTab, setDialogTab] = useState<"Configuration" | "Weights">("Weights")

    const activeVersion = scorecardData?.data.active_version

    const version = scorecardVersionData?.data

    // extracting the current major version
    const majorSelectedVersion = (selectedVersion ?? activeVersion?.version)?.split(".")?.[0]

    // show restore button when the current major version from the selected version/active version existed in our valid versions hashMap
    // and it's not the current active version
    const shouldShowRestoreButton =
      maxMinorVersionsMap[majorSelectedVersion as string] === (selectedVersion ?? activeVersion?.version) &&
      maxMinorVersionsMap[majorSelectedVersion as string] !== activeVersion?.version

    const tagVariant = activeVersion?.version === selectedVersion ? "green" : "blue"

    let adjustedCriteria: ScoreTableFormik["criteria"] = []

    if (version && version.criteria) {
      adjustedCriteria = version.criteria.map((criteria) =>
        criteria.map((item) => {
          const { rules } = convertComplexConditionsToNestedForm(item.condition)

          return { parsedCondition: ["", "=", ""], type: "string", ...item, ...rules[0]?.[0] }
        }),
      )
    }

    // simpler formik specific for editing the current scorecard
    const versionFormik = useFormik({
      initialValues: {
        name: formik?.values.name,
        uuid: formik?.values.uuid,
        format: version?.format,
        features: version?.features,
        criteria: adjustedCriteria,
        criteria_files: version?.criteria_files,
        results: version?.results,
        defaultScore: version?.default_score,
      },
      onSubmit: () => {
        return
      },
    })

    if (isScorecardVersionsListLoading || isScorecardVersionDataLoading) {
      return (
        <Grid container xs={12} gap={1} direction={"column"}>
          <Grid item className={"versioning-dialog-header"}>
            <Skeleton height={30} width={300} />
          </Grid>

          <Grid item px={2}>
            <ScorecardLoadingComponent />
          </Grid>
        </Grid>
      )
    }

    return (
      <Grid container>
        {/* Header */}
        <Grid
          item
          xs={12}
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          className={"versioning-dialog-header"}
        >
          <Grid item gap={1} xs={9} display="flex">
            <Typography variant="h2-bold" style={{ textTransform: "inherit", marginRight: "5px" }}>
              {scorecardData?.data?.name}
            </Typography>

            <VersionTag
              version={isNaN(Number(version?.version)) ? undefined : (Number(version?.version) ?? selectedVersion)}
              color={version ? tagVariant : "green"}
            />
          </Grid>

          {shouldShowRestoreButton && (
            <Button
              variant="secondary"
              size="small"
              onClick={() => changeVersion(selectedVersion as string)}
              disabled={versionSwitchMutation.isLoading}
              isLoading={versionSwitchMutation.isLoading}
            >
              {t("restoreBtn")}
            </Button>
          )}
        </Grid>

        {/* Body */}
        <Grid item xs={12} p={1.5} pt={0} mt={1}>
          <Grid container>
            <Grid container mt={1} gap={1} display={"flex"} justifyContent={"space-between"}>
              <Tabs value={dialogTab}>
                <Tab
                  label={t("tabelLabels.ruleWeights")}
                  value={t("weights")}
                  selected={dialogTab === "Weights"}
                  // To reset container image shifting from one mode to another
                  onClick={() => setDialogTab("Weights")}
                  size="large"
                />
                <Tab
                  label={t("tabelLabels.tableConfingurations")}
                  value={t("configuration")}
                  selected={dialogTab === "Configuration"}
                  // To reset container image shifting from one mode to another
                  onClick={() => setDialogTab("Configuration")}
                  size="large"
                />
              </Tabs>
            </Grid>

            <Grid container item display={"flex"} xs={12} mt={3} gap={2}>
              <FormikProvider value={versionFormik}>
                {dialogTab === "Configuration" ? (
                  <Fragment>
                    {versionFormik.values.criteria.map((_, index) => (
                      <Grid item xs={12} key={index}>
                        <ConfigCard index={index} mode={mode} />
                      </Grid>
                    ))}
                  </Fragment>
                ) : (
                  <WeightTable mode={mode} />
                )}
              </FormikProvider>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    )
  }

  const isActionBtnsDisabled = !!isMutating || isAllComponentsLoading

  const selectedScoreTable: SelectedComponent = useMemo(
    () => ({
      id: scorecardData?.data.uuid ?? "",
      type: COMPONENT_TYPES.SCORETABLE,
      data: scorecardData?.data as ScoreCardGroupRetrieve,
    }),
    [scorecardData?.data],
  )

  return (
    <Fragment>
      {/* Testing Dialog */}
      {isTestDialogOpen && (
        <ComponentTestingDialog
          isOpen={isTestDialogOpen}
          handleClose={() => setIsTestDialogOpen(false)}
          selectedComponent={selectedScoreTable}
        />
      )}
      {/* Versioning Dialog */}
      {isVersionDialogOpen && scorecardData?.data.active_version && (
        <VersioningDialog
          activeVersion={scorecardData?.data.active_version}
          setSelectedVersion={setSelectedVersion}
          selectedVersion={selectedVersion}
          isOpen={isVersionDialogOpen}
          versions={scorecardVersionsList?.pages || []}
          onClose={() => setIsVersionDialogOpen(false)}
          infiniteScrollProps={{
            hasNextPage,
            isFetchingNextPage,
            lastVersionRef,
          }}
          maxWidth="lg"
        >
          <ScoreTableVersioningContainer />
        </VersioningDialog>
      )}

      <Card className={styles.card}>
        <CardHeader
          className={styles.cardHeader}
          disableTypography
          title={
            <Grid container justifyContent={"space-between"} alignItems={"center"} maxWidth="100%">
              {mode !== "read" || (variant === "import" && !isActionBtnsDisabled) ? (
                <Grid container item xs={6} md={3} width="fit-content">
                  <InputText
                    id={`name`}
                    hideDescription
                    placeholder={t("inputNamePlaceholder")}
                    value={formik.values.name}
                    handleChange={formik.handleChange}
                    handleBlur={formik.handleBlur}
                    disabled={formik.isSubmitting}
                    fullWidth
                  />
                </Grid>
              ) : (
                <Grid container item xs={6} width="fit-content">
                  <Typography style={{ width: "100%" }} className={styles.cardTitle} noWrap variant="h3-bold">
                    {formik.values.name}
                  </Typography>
                </Grid>
              )}

              {canShowDetails && (
                <Grid container item xs={12} sm={6} gap={1} justifyContent="flex-end">
                  {mode === "read" && variant !== "import" ? (
                    <Fragment>
                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => duplicateScoreTable?.(scorecard as ScoreCardList)}
                        disabled={Boolean(formik.isSubmitting || disableDuplicateButton)}
                      >
                        {t("duplicateBtn")}
                      </Button>

                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => {
                          setSelectedVersion(scorecardData?.data?.active_version?.version)
                          setIsVersionDialogOpen(true)
                        }}
                      >
                        {t("historyBtn")}
                      </Button>

                      <Button
                        size="small"
                        variant="secondary"
                        onClick={() => setMode("edit")}
                        disabled={formik.isSubmitting || !RuleTablesEnabledFeatureFlag}
                        tooltip={!RuleTablesEnabledFeatureFlag ? "Score tables are currently unavailable" : undefined}
                      >
                        {t("editBtn")}
                      </Button>
                    </Fragment>
                  ) : variant === "import" ? (
                    <Fragment>
                      <Button
                        variant="secondary"
                        size="small"
                        disabled={isActionBtnsDisabled}
                        onClick={() => {
                          if (!uuid) return
                          removeComponent?.(uuid)
                          setTotalNumOfCurrentProjComponents?.((prev) => --prev)
                        }}
                        tooltipPlacement="top"
                        tooltip={t("excluedComponentToolTip")}
                      >
                        {t("dismissBtn")}
                      </Button>

                      <Button
                        variant="primary"
                        size="small"
                        disabled={isActionBtnsDisabled || !isPermittedToCloneFiles}
                        onClick={saveImportedScoreTableHandler}
                        isLoading={isActionBtnsDisabled}
                      >
                        {t("saveBtn")}
                      </Button>
                    </Fragment>
                  ) : (
                    <Fragment>
                      <Button
                        disabled={formik.isSubmitting}
                        onClick={handleCancelEdit}
                        size="small"
                        variant={"secondary"}
                      >
                        {t("cancelBtn")}
                      </Button>

                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => formik.submitForm()}
                        disabled={formik.isSubmitting || !formik.isValid || hasDuplicateFeature}
                        isLoading={formik.isSubmitting}
                      >
                        {t("saveBtn")}
                      </Button>
                    </Fragment>
                  )}
                </Grid>
              )}
            </Grid>
          }
          action={
            mode === "read" &&
            variant !== "import" && (
              <ScorecardMenu
                name={formik.values.name}
                uuid={formik.values.uuid}
                mode="ScoreTable"
                setIsTestDialogOpen={setIsTestDialogOpen}
                canRemoveScorecard={canRemoveScorecard}
                canShowDetails={canShowDetails}
              />
            )
          }
        />

        <CardContent className={styles.cardContent}>
          {canShowDetails ? (
            <Grid container>
              <Grid item direction={"column"} xs={12} gap={1}>
                <Grid
                  container
                  gap={1}
                  display={"flex"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                  flexWrap={"nowrap"}
                >
                  <Tabs value={tab}>
                    <Tab
                      label={t("tabelLabels.ruleWeights")}
                      value={t("weights")}
                      selected={tab === "Weights"}
                      // To reset container image shifting from one mode to another
                      onClick={() => setTab("Weights")}
                      size="large"
                    />
                    <Tab
                      label={t("tabelLabels.tableConfingurations")}
                      value={t("configuration")}
                      selected={tab === "Configuration"}
                      // To reset container image shifting from one mode to another
                      onClick={() => setTab("Configuration")}
                      size="large"
                    />
                  </Tabs>

                  {tab === "Configuration" && mode !== "read" && (
                    <Button
                      variant="secondary"
                      onClick={handleAddingBucket}
                      disabled={
                        isTableFormatFormikValues(formik.values) &&
                        formik?.values?.criteria?.length >= MaxFeaturesFeatureFlag &&
                        MaxFeaturesFeatureFlag !== -1
                      }
                      tooltip={
                        isTableFormatFormikValues(formik.values) &&
                        formik?.values?.criteria?.length >= MaxFeaturesFeatureFlag &&
                        MaxFeaturesFeatureFlag !== -1
                          ? t("maxNumofFeaturesTooltip", {
                              maxNum: MaxFeaturesFeatureFlag,
                            })
                          : undefined
                      }
                    >
                      {t("addFeatureBtn")}
                    </Button>
                  )}
                </Grid>

                {tab === "Configuration" && mode !== "read" && (
                  <Grid item xs={12} justifyItems={"flex-end"}>
                    <Typography variant="label-bold">{t("addFeatureHelper")}</Typography>
                  </Grid>
                )}
              </Grid>

              <Grid container item display={"flex"} xs={12} mt={2} gap={2}>
                {isScorecardDataLoading || isScorecardDataRefetching ? (
                  <Grid container direction="column" justifyContent="flex-start" alignItems="center" spacing={2}>
                    <ScorecardLoadingComponent />
                  </Grid>
                ) : (
                  <FormikProvider value={formik}>
                    {tab === "Configuration" ? (
                      <Fragment>
                        {isTableFormatFormikValues(formik.values) &&
                          formik.values.criteria.map((_, index) => (
                            <Grid item xs={12} key={index}>
                              <ConfigCard index={index} mode={mode} />
                            </Grid>
                          ))}
                      </Fragment>
                    ) : (
                      <WeightTable mode={mode} />
                    )}
                  </FormikProvider>
                )}
              </Grid>
            </Grid>
          ) : (
            <Grid
              container
              padding={2}
              border={1}
              borderColor={"var(--base-border-1)"}
              bgcolor={"var(--base-background-1)"}
              borderRadius={1}
            >
              <Typography variant="label-regular" color="base">
                {t("hiddenDuePermission")}
              </Typography>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Fragment>
  )
}
